import {
  FINDING_SCORE_THRESHOLD,
  HIGH_SCORE_THRESHOLD,
} from '@/components/policylist/constants/builtin-catalog';
import type { AlertScoreBreakdown } from '@kirin/api-client/contracts/alerts';
import {
  ExtensionFindingType,
  type ScoreContributor,
} from '@kirin/api-client/contracts/analyses';
import type { ReactElement } from 'react';
import { Fragment } from 'react';

interface WhyThisFiredProps {
  type: string;
  scoreBreakdown?: AlertScoreBreakdown;
}

function isCustomCapabilityRule(type: string): boolean {
  return type.toLowerCase().includes(ExtensionFindingType.CustomCapabilityRule);
}

function scoreLevel(score: number): string {
  if (score >= HIGH_SCORE_THRESHOLD) return 'High';
  if (score >= FINDING_SCORE_THRESHOLD) return 'Medium';
  return 'Low';
}

/**
 * Narrative under the weight formula — mirrors the KIR-3781 mock
 * (group winners keep max weight; ungrouped fields add).
 */
function buildAggregateExplanation(
  contributors: ScoreContributor[],
  aggregateScore: number
): string {
  const parts: string[] = [];
  for (const contributor of contributors) {
    if (contributor.correlationGroup) {
      parts.push(
        `The ${contributor.correlationGroup} correlation group kept its highest weight (${contributor.weight}).`
      );
    } else {
      parts.push(
        `The ungrouped capability ${contributor.field} added ${contributor.weight}.`
      );
    }
  }
  parts.push(
    `Total ${aggregateScore} crossed the finding threshold of ${FINDING_SCORE_THRESHOLD}.`
  );
  return parts.join(' ');
}

function ContributorWeightChip({
  contributor,
}: {
  contributor: ScoreContributor;
}): ReactElement {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-background px-2 py-1 shadow-sm">
      <code className="text-[11px] font-medium text-foreground">
        {contributor.field}
      </code>
      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
        weight {contributor.weight}
      </span>
    </span>
  );
}

/**
 * Explains the catalog fields that contributed to an extension finding without
 * reusing the severity-derived alert score.
 */
export function WhyThisFired({
  type,
  scoreBreakdown,
}: WhyThisFiredProps): ReactElement | null {
  const contributors = scoreBreakdown?.contributors ?? [];
  const aggregateScore = scoreBreakdown?.score;
  const isInstantSignal =
    aggregateScore === undefined &&
    contributors.length === 1 &&
    contributors[0]?.weight === 100;

  if (
    contributors.length === 0 ||
    isCustomCapabilityRule(type) ||
    (aggregateScore === undefined && !isInstantSignal)
  ) {
    return null;
  }

  return (
    <section className="mt-3" data-testid="why-this-fired">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Why this fired
      </h3>
      <div className="mt-2 rounded-lg bg-slate-100 px-3 py-3 dark:bg-slate-700/50">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-2 text-sm">
          {contributors.map((contributor, index) => (
            <Fragment key={`${contributor.ruleId}-${contributor.field}`}>
              {index > 0 && (
                <span className="text-muted-foreground" aria-hidden>
                  +
                </span>
              )}
              <ContributorWeightChip contributor={contributor} />
            </Fragment>
          ))}
          {aggregateScore !== undefined && (
            <span className="font-semibold text-foreground">
              = {aggregateScore} ({scoreLevel(aggregateScore)})
            </span>
          )}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          {aggregateScore !== undefined
            ? buildAggregateExplanation(contributors, aggregateScore)
            : 'Instant signal: this field carries weight 100.'}
        </p>
      </div>
    </section>
  );
}

WhyThisFired.displayName = 'WhyThisFired';
